// notesSlice.ts
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchCoachNoteTopicsAsync,
  fetchCoachNotesAsync,
  fetchAllCoachNotesAsync,
  saveCoachNoteAsync,
  updateCoachNoteAsync,
  uploadAttachmentAsync,
} from "../thunks/NotesThunk";
import { CoachNote, CoachNoteAttachment } from "../../types/NotesType";
import { TopicCategory } from "../../services/NotesService";

type AsyncStatus = "idle" | "loading" | "succeeded" | "failed";

interface NotesState {
  notes: CoachNote[];
  topics: TopicCategory[];
  uploadedAttachments: CoachNoteAttachment[];
  status: AsyncStatus;
  topicsStatus: AsyncStatus;
  saveNoteStatus: AsyncStatus;
  uploadStatus: AsyncStatus;
  error: string | null;
  saveError: string | null;
  uploadError: string | null;
  statusCode: number | null;
  totalPages: number;
  totalItems: number;
  currentUserId: number | null;
}

const initialState: NotesState = {
  notes: [],
  topics: [],
  uploadedAttachments: [],
  status: "idle",
  topicsStatus: "idle",
  saveNoteStatus: "idle",
  uploadStatus: "idle",
  error: null,
  saveError: null,
  uploadError: null,
  statusCode: null,
  totalPages: 0,
  totalItems: 0,
  currentUserId: null,
};

const notesSlice = createSlice({
  name: "notes",
  initialState,
  reducers: {
    clearSaveNoteStatus: (state) => {
      state.saveNoteStatus = "idle";
      state.saveError = null;
    },
    clearUploadStatus: (state) => {
      state.uploadStatus = "idle";
      state.uploadError = null;
    },
    removeUploadedAttachment: (state, action) => {
      state.uploadedAttachments = state.uploadedAttachments.filter(
        (attachment) => attachment.id !== action.payload
      );
    },
    clearUploadedAttachments: (state) => {
      state.uploadedAttachments = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch coach notes
      .addCase(fetchCoachNotesAsync.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchCoachNotesAsync.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.notes = action.payload.items || action.payload;
        state.totalPages = action.payload.total_pages || 0;
        state.totalItems = action.payload.total_items || 0;
        state.currentUserId = action.meta.arg.userId;
      })
      .addCase(fetchCoachNotesAsync.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })

      // Fetch all coach notes (no pagination)
      .addCase(fetchAllCoachNotesAsync.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAllCoachNotesAsync.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.notes = action.payload.items;
      })
      .addCase(fetchAllCoachNotesAsync.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })

      // Fetch coach note topics
      .addCase(fetchCoachNoteTopicsAsync.pending, (state) => {
        state.topicsStatus = "loading";
        state.error = null;
      })
      .addCase(fetchCoachNoteTopicsAsync.fulfilled, (state, action) => {
        state.topicsStatus = "succeeded";
        state.topics = action.payload;
      })
      .addCase(fetchCoachNoteTopicsAsync.rejected, (state, action) => {
        state.topicsStatus = "failed";
        state.error = action.payload as string;
      })

      // Save coach note
      .addCase(saveCoachNoteAsync.pending, (state) => {
        state.saveNoteStatus = "loading";
        state.saveError = null;
      })
      .addCase(saveCoachNoteAsync.fulfilled, (state, action) => {
        state.saveNoteStatus = "succeeded";
        // Add the new note to the beginning of the list
        state.notes.unshift(action.payload);
        state.totalItems += 1;
        // Clear uploaded attachments after successful save
        state.uploadedAttachments = [];
      })
      .addCase(saveCoachNoteAsync.rejected, (state, action) => {
        state.saveNoteStatus = "failed";
        state.saveError = action.payload as string;
      })

      // Update coach note
      .addCase(updateCoachNoteAsync.pending, (state) => {
        state.saveNoteStatus = "loading";
        state.saveError = null;
      })
      .addCase(updateCoachNoteAsync.fulfilled, (state, action) => {
        state.saveNoteStatus = "succeeded";
        // Update the existing note in the list
        const noteIndex = state.notes.findIndex(
          (note) => note.id === action.payload.id
        );
        if (noteIndex !== -1) {
          state.notes[noteIndex] = action.payload;
        }
      })
      .addCase(updateCoachNoteAsync.rejected, (state, action) => {
        state.saveNoteStatus = "failed";
        state.saveError = action.payload as string;
      })

      // Upload attachment
      .addCase(uploadAttachmentAsync.pending, (state) => {
        state.uploadStatus = "loading";
        state.uploadError = null;
      })
      .addCase(uploadAttachmentAsync.fulfilled, (state, action) => {
        state.uploadStatus = "succeeded";
        // Add the uploaded attachment to the list
        state.uploadedAttachments.push(action.payload);
      })
      .addCase(uploadAttachmentAsync.rejected, (state, action) => {
        state.uploadStatus = "failed";
        state.uploadError = action.payload as string;
      });
  },
});

export const {
  clearSaveNoteStatus,
  clearUploadStatus,
  removeUploadedAttachment,
  clearUploadedAttachments,
} = notesSlice.actions;

export default notesSlice.reducer;
